/**
 * CSV export (plan page 2).
 *
 * Excel on Windows needs a UTF-8 BOM to read Persian text, and every field is
 * quoted+escaped because device hostnames and TR-069 paths contain commas and
 * quotes. Export covers *the page you are looking at* — the contract has no
 * bulk export endpoint, and the button says so.
 */

export interface CsvColumn<T> {
  header: string;
  value: (row: T) => string | number | null | undefined;
}

/**
 * Spreadsheet formula injection (OWASP «CSV injection»): a hostname, a subscriber name or a label that
 * begins with `=`, `+`, `-`, `@` (or a tab / CR) is EVALUATED when the file is opened in Excel. A text
 * cell of that shape gets a leading `'`; a value that is plainly a number or a phone number
 * (`+98912…`, `-5`) is not a formula and is left alone. The server's report exports apply the same
 * rule (`acs_service/reports.py::neutralise`) — one rule, two writers.
 */
export function neutraliseFormula(text: string): string {
  const lead = text.charAt(0);
  const formulaLike =
    lead === '=' || lead === '+' || lead === '-' || lead === '@' || lead === '\t' || lead === '\r';
  return formulaLike && !/^[+-]?[0-9][0-9 .,()\-/]*$/.test(text) ? `'${text}` : text;
}

function escapeCell(raw: string): string {
  const value = neutraliseFormula(raw);
  const needsQuotes = /[",\n\r]/.test(value);
  const escaped = value.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
}

export function buildCsv<T>(rows: readonly T[], columns: readonly CsvColumn<T>[]): string {
  const header = columns.map((c) => escapeCell(c.header)).join(',');
  const body = rows.map((row) =>
    columns
      .map((column) => {
        const value = column.value(row);
        return escapeCell(value === null || value === undefined ? '' : String(value));
      })
      .join(','),
  );
  return [header, ...body].join('\r\n');
}

/** `\uFEFF` BOM so Excel reads Persian headers as UTF-8. */
export function csvBlobUrl(csv: string): string {
  const withBom = '\uFEFF' + csv;
  const blob = new Blob([withBom], { type: 'text/csv;charset=utf-8' });
  return URL.createObjectURL(blob);
}

/** Trigger a browser download for the current page of results. */
export function downloadCsv(csv: string, fileName: string): void {
  const url = csvBlobUrl(csv);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Filename carries the fleet scope + UTC date so exports are tellable apart. */
export function csvFileName(scope: string, now = new Date()): string {
  const stamp = now.toISOString().slice(0, 16).replace(/[-:]/g, '').replace('T', '_');
  return `acs-${scope}-${stamp}.csv`;
}
